import { Screen, StackScreenProps } from '@rahtab/stack-navigator';
import { withNumbers } from '@rahtab/system/lib/tools/string';
import {
  BottomSheet,
  Button,
  ButtonBase,
  Card,
  CardCaption,
  CardContainer,
  Colors,
  DatePicker,
  getRadius,
  getSpacing,
  HorizontalLine,
  Text,
  TextInput,
  TextView,
  ToggleButton,
} from '@rahtab/ui';
import ChevronLeftIcon from '@rahtab/ui/dist/icons/ChevronLeftIcon';
import ErrorCard from 'components/ErrorCard';
import LabeledText from 'components/LabeledText';
import ProvincePicker from 'components/ProvincePicker';
import type { RootStackType } from 'components/Router';
import useCheckRequestId, { RequestStatusCode } from 'hooks/useCheckRequestId';
import useFindSupplier from 'hooks/useFindSupplier';
import useSaveInvalidRequest from 'hooks/useSaveInvalidRequest';
import useVetApproveRequest from 'hooks/useVetApproveRequest';
import moment from 'moment-jalaali';
import React, { useEffect, useState } from 'react';
import { ScrollView, StyleSheet, View } from 'react-native';
import { withSeparator } from '../tools/string';

const DATE_FORMAT = 'YYYY/MM/DD';
const DISPLAY_DATE_FORMAT = 'jYYYY/jMM/jDD';

type SubmitLoadInfoProps = StackScreenProps<RootStackType, 'SubmitLoadInfo'>;

const SubmitLoadInfo: React.FC<SubmitLoadInfoProps> = ({ route }) => {
  const [isBottomSheetVisible, setIsBottomSheetVisible] = useState(false);
  const { RequestId = '', fromHistory } = route.params || {};
  const [date, setDate] = useState(moment().format(DATE_FORMAT));
  const [quantity, setQuantity] = useState('');
  const [count, setCount] = useState('');
  const [province, setProvince] = useState('');
  const [supplierIdentification, setSupplierIdentification] = useState('');
  const [invalidLoadType, setInvalidLoadType] = useState<'بار با مجوز حمل' | 'بار بدون مجوز حمل' | ''>('');
  const { loading: checkingRequestId, data: checkRequest } = useCheckRequestId({ RequestId }, { skip: !RequestId });
  const isApprovableOrExists = !!RequestId && checkRequest?.resultCode !== RequestStatusCode.Invalid;
  const isApprovable = checkRequest?.resultCode === RequestStatusCode.Approvable;
  const [findSupplier, { loading: findingSupplier, data: supplier, error: findSupplierError }] = useFindSupplier({
    Identification: supplierIdentification,
  });
  const [saveInvalidRequest, { loading: savingInvalidRequest }] = useSaveInvalidRequest(
    {
      assignerCode: checkRequest?.assignerCode || '',
      measureCode: '3',
      receiveDate: date,
      reqAmount: quantity,
      supplierCode:
        supplier?.SupplierCode ||
        checkRequest?.supplierCode ||
        (!RequestId && invalidLoadType === 'بار با مجوز حمل' && !supplier && '0') ||
        (!RequestId && invalidLoadType === 'بار بدون مجوز حمل' && '-1') ||
        '',
      requestCode: checkRequest?.requestCode || '',
      itemCount: parseInt(count, 10),
      otherSupplierIdentification: supplierIdentification || '',
      otherSupplierProvince: province,
    },
    fromHistory,
  );
  const [vetApproveRequest] = useVetApproveRequest(
    {
      receivedDate: date,
      reqAmount: checkRequest?.reqAmount || '',
      requestCode: checkRequest?.requestCode || '',
      vetApproveAmount: quantity,
      itemCount: parseInt(count, 10),
    },
    fromHistory,
  );

  useEffect(() => {
    setSupplierIdentification('');
  }, [invalidLoadType]);

  const loading = isApprovableOrExists ? checkingRequestId : false;
  const transparentLoading = findingSupplier || savingInvalidRequest;
  const isInputValid: boolean =
    (!!supplier ||
      (invalidLoadType === 'بار با مجوز حمل' && !!supplierIdentification) ||
      invalidLoadType === 'بار بدون مجوز حمل' ||
      !!checkRequest?.supplierName) &&
    !!date &&
    !!quantity &&
    !!count &&
    (!!province ||
      invalidLoadType === 'بار بدون مجوز حمل' ||
      (invalidLoadType === 'بار با مجوز حمل' && !!supplier) ||
      !!RequestId);

  useEffect(() => {
    if (!!RequestId) return;
    setInvalidLoadType('بار با مجوز حمل');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (supplier) setIsBottomSheetVisible(false);
  }, [supplier]);

  useEffect(() => {
    if (!findSupplierError?.message || invalidLoadType !== 'بار با مجوز حمل') return;
    setIsBottomSheetVisible(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [findSupplierError]);

  return (
    <Screen
      title={RequestId ? 'نتیجه استعلام شناسه سفارش' : 'ثبت اطلاعات بار ورودی بدون شناسه'}
      loading={loading}
      transparentLoading={transparentLoading}
      data={{ checkRequest, supplier }}
      renderControls={
        RequestId
          ? ({ checkRequest }) => {
              return (
                <TextView
                  marginBottom={0}
                  borderColor="default"
                  valueColor={checkRequest?.result ? 'primary' : 'secondary'}
                  title="شناسه سفارش استعلام شده"
                  value={RequestId}
                />
              );
            }
          : undefined
      }
      renderMain={({ checkRequest, supplier }) => {
        return (
          <ScrollView keyboardShouldPersistTaps="handled">
            <BottomSheet visible={isBottomSheetVisible} onRequestClose={() => setIsBottomSheetVisible(false)}>
              <View>
                <Text>
                  شناسه یکتای واحد مرغداری که در گواهی حمل بار دامپزشکی درج شده است را وارد نموده و سپس نتیجه را بررسی
                  نمایید.
                </Text>
                <TextInput
                  placeholder="شناسه یکتا واحد مرغداری را وارد نمایید."
                  label="شناسه یکتا"
                  value={supplierIdentification}
                  onChangeText={setSupplierIdentification}
                  helperText={RequestId && findSupplierError?.message}
                  keyboardType="numeric"
                />
                <Button
                  color="primary"
                  text="جست و جوی واحد مرغداری"
                  disabled={!supplierIdentification}
                  onPress={findSupplier}
                />
              </View>
            </BottomSheet>
            {!!RequestId && !isApprovable && !checkRequest?.result && (
              <ErrorCard title={checkRequest?.error!} message={checkRequest?.comment!} />
            )}
            <Card style={styles.card}>
              <CardCaption>
                <Text variant="cardTitle" margin="none" color="primary" style={styles.captionText}>
                  {isApprovable ? 'مشخصات سفارش / مرسوله استعلام شده' : 'ثبت اطلاعات بار دریافتی'}
                </Text>
              </CardCaption>
              <CardContainer>
                {isApprovableOrExists && (
                  <>
                    <LabeledText label="تخصیص دهنده">{checkRequest?.assignerName}</LabeledText>
                    <LabeledText label="واحد مرغداری">{checkRequest?.supplierName}</LabeledText>
                    <LabeledText label="مقدار وزنی سفارش / مرسوله">
                      {withSeparator(checkRequest!.reqAmount)} {checkRequest?.reqMeasure}
                    </LabeledText>
                    <LabeledText label="تعداد قطعات مرغ زنده ورودی">
                      {withSeparator(checkRequest!.templateCount)} قطعه
                    </LabeledText>
                    <LabeledText label="تاریخ تحویل">{checkRequest?.deliveryDate}</LabeledText>
                    <LabeledText label="کشتارگاه مقصد">{checkRequest?.reqSupplierName}</LabeledText>
                    <HorizontalLine />
                  </>
                )}
                {!isApprovableOrExists && (
                  <>
                    {!RequestId && (
                      <ToggleButton color="secondary" selected={invalidLoadType} onPress={setInvalidLoadType as any}>
                        <ToggleButton.Item label="بار با مجوز حمل" />
                        <ToggleButton.Item label="بار بدون مجوز حمل" />
                      </ToggleButton>
                    )}
                    {invalidLoadType !== 'بار بدون مجوز حمل' && (
                      <>
                        <ButtonBase
                          style={StyleSheet.flatten([styles.cardContainer])}
                          onPress={() => setIsBottomSheetVisible(true)}>
                          <Text style={styles.title} margin="none" color="description">
                            {!supplier && !supplierIdentification && (
                              <Text margin="none" color="description">
                                واحد مرغداری را انتخاب نمایید.
                              </Text>
                            )}
                            {!!supplier && <LabeledText label="واحد مرغداری">{supplier.SupplierName}</LabeledText>}
                            {!supplier && !!supplierIdentification && (
                              <LabeledText label="شناسه یکتای واحد مرغداری">{supplierIdentification}</LabeledText>
                            )}
                          </Text>
                          <ChevronLeftIcon stroke={Colors.primary} style={{ transform: [{ rotate: '-90deg' }] }} />
                        </ButtonBase>
                        {!!supplier && <TextView title="شناسه یکتای واحد مرغداری" value={supplierIdentification} />}
                        {!RequestId && !!supplierIdentification && !supplier && (
                          <ProvincePicker selected={province} onSelectValue={setProvince} />
                        )}
                      </>
                    )}
                  </>
                )}
                <View style={styles.rowView}>
                  <TextInput
                    value={quantity}
                    onChangeText={withNumbers(setQuantity)}
                    placeholder="مقدار بار ورودی را وارد نمایید."
                    style={{ flex: 1 }}
                    keyboardType="numeric"
                  />
                  <TextView style={[styles.textView, { width: 67 }]} value={checkRequest?.reqMeasure || 'کیلوگرم'} />
                </View>
                <View style={styles.rowView}>
                  <TextInput
                    value={count}
                    onChangeText={withNumbers(setCount)}
                    placeholder="تعداد بار ورودی را وارد نمایید."
                    style={{ flex: 1 }}
                    keyboardType="numeric"
                  />
                  <TextView style={[styles.textView, { width: 55 }]} value="قطعه" />
                </View>
                <DatePicker
                  label="تاریخ وصول"
                  placeholder="تاریخ وصول بار ورودی را وارد نمایید."
                  value={date}
                  onConfirm={setDate}
                  format={DATE_FORMAT}
                  displayFormat={DISPLAY_DATE_FORMAT}
                  disabled
                />
                <Button
                  color="primary"
                  text={isApprovable ? 'تایید بار ورودی' : 'ثبت اطلاعات بار ورودی'}
                  onPress={isApprovableOrExists ? vetApproveRequest : saveInvalidRequest}
                  disabled={!isInputValid}
                />
              </CardContainer>
            </Card>
          </ScrollView>
        );
      }}
    />
  );
};

export default SubmitLoadInfo;

const styles = StyleSheet.create({
  card: {
    margin: getSpacing(),
    marginBottom: 0,
  },
  cardContainer: {
    borderRadius: getRadius(),
    backgroundColor: Colors.background,
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: getSpacing(),
    height: getSpacing('row'),
    justifyContent: 'space-between',
    marginBottom: getSpacing(),
  },
  title: {
    flex: 1,
    marginRight: getSpacing(0.5),
  },
  captionText: {
    textAlign: 'center',
    // flex: 1,
  },
  rowView: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  textView: {
    borderColor: Colors.midGray,
    marginStart: getSpacing(),
    justifyContent: 'center',
  },
  justifiedText: {
    textAlign: 'justify',
  },
  cardIcon: {
    // marginRight: getSpacing(0.6),
    marginTop: getSpacing(),
  },
});
